import { Ref, useCallback, useImperativeHandle, useState } from "react";
import TextModal from "@coworker/apprestructured/src/task-management/components/TextModal/TextModal";
import {
  CreatorFragmentCommonProps,
  CreatorFragmentHandle,
} from "../CreatorFragment";
import {
  getByPath,
  setByPath,
} from "@coworker/apprestructured/src/shared/helpers/object.helper";
import { validateInitialDetailsFieldValue } from "@coworker/apprestructured/src/shared/helpers/schemaValidator";
import { isLoading } from "@coworker/apprestructured/src/task-management/loading";
import LoadingCreatorFragment from "@coworker/apprestructured/src/task-management/components/LoadingCreatorFragment/LoadingCreatorFragment";
import { useTranslation } from "@coworker/locales";
import { FixaInputField } from "@coworker/apprestructured/src/shared/wrappers/FixaInputField/FixaInputField";
import { FixaText } from "@coworker/apprestructured/src/shared/wrappers/FixaText/FixaText";
import InputWrapper from "@coworker/apprestructured/src/task-management/components/InputWrapper/InputWrapper";
import styles from "./NumberInputCreatorFragment.module.css";

export type NumberInputCreatorFragmentConfigProps = {
  field: string;
  title: string;
  min?: number;
  max?: number;
  inline?: boolean;
};

type Props = {
  configProps: NumberInputCreatorFragmentConfigProps;
  commonProps: CreatorFragmentCommonProps;
  ref?: Ref<CreatorFragmentHandle>;
};

function NumberInputCreatorFragment({ configProps, commonProps, ref }: Props) {
  const { field, title, min = 1, max = 999, inline = false } = configProps;
  const { task, onTaskChange, blockInput, showValidation, taskType } =
    commonProps;
  const { t } = useTranslation();
  const rawValue = validateInitialDetailsFieldValue<number>(
    getByPath(task, field),
    field,
    taskType
  );
  const value = isLoading(rawValue)
    ? ""
    : rawValue == null
      ? ""
      : String(rawValue);
  const isValid =
    isLoading(rawValue) ||
    (rawValue != null && rawValue >= min && rawValue <= max);

  useImperativeHandle(ref, () => ({ valid: isValid }));

  const [showMaxError, setShowMaxError] = useState(false);

  const handleInlineChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const raw = event.target.value;
      if (raw !== "" && !/^\d+$/.test(raw)) {
        //Block anything other than digits, also block pasting.
        return;
      }
      if (raw === "") {
        setShowMaxError(false);
        onTaskChange(setByPath(field, null));
        return;
      }
      const parsed = Number(raw);
      if (parsed > max) {
        setShowMaxError(true);
        return;
      }
      setShowMaxError(false);
      onTaskChange(setByPath(field, parsed));
    },
    [field, max, onTaskChange]
  );

  const handleConfirm = useCallback(
    (newValue: string) => onTaskChange(setByPath(field, Number(newValue))),
    [field, onTaskChange]
  );

  if (isLoading(rawValue)) return <LoadingCreatorFragment />;

  if (inline) {
    const inlineError =
      showMaxError || (showValidation && !isValid)
        ? t("addQuantityNumberString", { maxQuantity: max })
        : null;
    return (
      <div className={styles["inline"]}>
        <div className={styles["inlineContent"]}>
          <FixaText textStyle="Body.M">{t(title)}</FixaText>
          <InputWrapper error={inlineError}>
            <FixaInputField
              id={field}
              type="text"
              inputMode="numeric"
              min={String(min)}
              max={String(max)}
              suffixLabel={t("piecesString")}
              value={value}
              disabled={blockInput}
              onChange={handleInlineChange}
            />
          </InputWrapper>
        </div>
      </div>
    );
  }

  const validate = (newValue: string) => {
    const parsed = Number(newValue);
    return Number.isInteger(parsed) && parsed >= min && parsed <= max;
  };

  return (
    <TextModal
      title={t(title)}
      text={value}
      onConfirmClick={handleConfirm}
      disabled={blockInput}
      maxLength={String(max).length}
      inputLabel={t(title)}
      inputMode="numeric"
      numericOnly
      validateOnConfirm={validate}
      validationErrorText={t("pleaseFillInString")}
    />
  );
}

export default NumberInputCreatorFragment;
